import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import VerLinksButton from './VerLinksButton';
import TicketMedia from './TicketMedia';

// En desarrollo el backend corre en localhost:5000; en producción el socket se
// conecta al mismo origen donde se sirve la app.
const SOCKET_BASE = process.env.NODE_ENV === 'production'
  ? window.location.origin
  : 'http://localhost:5000';

const PAGE_SIZE = 20;
const STATUSES = ['ABIERTO', 'EN CURSO', 'BLOQUEADO', 'FINALIZADO'];

const STATUS_COLOR = {
  ABIERTO: '#60a5fa',
  'EN CURSO': '#f59e0b',
  BLOQUEADO: '#ef4444',
  FINALIZADO: '#22c55e',
};
const ROW_BG = {
  ABIERTO: 'transparent',
  'EN CURSO': 'rgba(245,158,11,0.10)',
  BLOQUEADO: 'rgba(239,68,68,0.10)',
  FINALIZADO: 'rgba(34,197,94,0.10)',
};

function Tickets({ token }) {
  const isMobile = useIsMobile();
  const authH = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [attachment, setAttachment] = useState(null); // { url, type, name }
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const commentsEndRef = useRef(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (statusFilter) params.set('status', statusFilter);
    if (userFilter) params.set('user_id', userFilter);
    fetch(`/api/tickets?${params.toString()}`, { headers: authH })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, userFilter, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // El socket se crea una sola vez (depende solo del token); estos refs siempre
  // apuntan al estado actual para no reconectar al cambiar de página/filtros.
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;
  const detailRef = useRef(detail);
  detailRef.current = detail;

  // Tiempo real: cuando un ticket cambia en el backend (lo crea/comenta el cliente
  // desde el portal público, el bot, u otro admin), el servidor emite
  // 'ticket_changed' a la empresa. Refrescamos la lista y, si hay un ticket
  // abierto, también su detalle (sin "Cargando") para ver el comentario al vuelo.
  useEffect(() => {
    if (!token) return;
    const socket = io(`${SOCKET_BASE}/admin`, { auth: { token } });
    socket.on('ticket_changed', () => {
      fetchRef.current();
      const open = detailRef.current;
      if (!open?.id) return;
      fetch(`/api/tickets/${open.id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(j => { if (j) setDetail(j); })
        .catch(() => {});
    });
    return () => socket.disconnect();
  }, [token]);

  useEffect(() => {
    fetch('/api/tickets/users', { headers: authH })
      .then(r => r.ok ? r.json() : [])
      .then(d => setUsers(Array.isArray(d) ? d : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (commentsEndRef.current) commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.comments?.length]);

  const openDetail = async (id) => {
    setLoadingDetail(true);
    setDetail({ id, comments: [] });
    setFeedback('');
    setAttachment(null);
    setNewComment('');
    const r = await fetch(`/api/tickets/${id}`, { headers: authH });
    const json = r.ok ? await r.json() : null;
    setDetail(json);
    setLoadingDetail(false);
  };

  const patchTicket = async (changes) => {
    setBusy(true);
    const r = await fetch(`/api/tickets/${detail.id}`, {
      method: 'PATCH',
      headers: { ...authH, 'Content-Type': 'application/json' },
      body: JSON.stringify(changes),
    });
    if (r.ok) {
      setDetail(prev => ({ ...prev, ...changes }));
      setData(prev => prev.map(t => t.id === detail.id
        ? { ...t, ...('status' in changes ? { status: changes.status } : {}), ...('user_id' in changes ? { user_id: changes.user_id, assigned_username: users.find(u => String(u.id) === String(changes.user_id))?.username || null } : {}) }
        : t));
    }
    setBusy(false);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setFeedback('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch('/api/archives/upload', { method: 'POST', headers: authH, body: fd });
      const json = await r.json().catch(() => ({}));
      if (r.ok && json.url) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const type = file.type.startsWith('image/') ? 'imagen'
          : file.type.startsWith('video/') ? 'video'
          : file.type.startsWith('audio/') ? 'audio'
          : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? 'imagen'
          : ['mp4', 'webm', 'mov'].includes(ext) ? 'video'
          : 'documento';
        setAttachment({ url: json.url, type, name: file.name });
      } else {
        setFeedback(json.message || 'Error al subir el archivo');
      }
    } catch {
      setFeedback('Error al subir el archivo');
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const sendComment = async () => {
    const text = newComment.trim();
    if (!text && !attachment) return;
    setBusy(true);
    const body = { text };
    if (attachment) { body.media_url = attachment.url; body.media_type = attachment.type; body.media_name = attachment.name; }
    const r = await fetch(`/api/tickets/${detail.id}/comments`, {
      method: 'POST',
      headers: { ...authH, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      const { comment } = await r.json();
      setDetail(prev => ({ ...prev, comments: [...(prev.comments || []), comment] }));
      setNewComment('');
      setAttachment(null);
    }
    setBusy(false);
  };

  const sendToClient = async (cid) => {
    setBusy(true);
    setFeedback('');
    const r = await fetch(`/api/tickets/${detail.id}/comments/${cid}/send`, {
      method: 'POST', headers: authH,
    });
    const json = await r.json().catch(() => ({}));
    if (r.ok) {
      setDetail(prev => ({ ...prev, comments: prev.comments.map(c => c.id === cid ? json.comment : c) }));
      setFeedback('✓ Comentario enviado al cliente');
    } else {
      setFeedback(json.message || 'Error al enviar');
    }
    setBusy(false);
  };

  const toggleVisible = async (cid, visible) => {
    const r = await fetch(`/api/tickets/${detail.id}/comments/${cid}`, {
      method: 'PATCH',
      headers: { ...authH, 'Content-Type': 'application/json' },
      body: JSON.stringify({ visible_to_client: visible }),
    });
    if (r.ok) {
      const { comment } = await r.json();
      setDetail(prev => ({ ...prev, comments: prev.comments.map(c => c.id === cid ? comment : c) }));
    }
  };

  const selectStyle = {
    backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text, borderRadius: '6px', padding: '6px 10px', fontSize: '0.85rem',
    cursor: 'pointer', outline: 'none',
  };

  return (
    <div style={{ padding: isMobile ? '1.2rem 0.8rem' : '2rem', maxWidth: '1300px', margin: '0 auto' }}>
      <h2 style={{ color: theme.colors.primary, margin: '0 0 1.2rem' }}>Tickets de soporte</h2>

      <div style={{ marginBottom: '1.2rem' }}>
        <VerLinksButton />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.75rem', marginBottom: '4px' }}>Filtrar por estado</label>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
            <option value="">Todos los estados</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.75rem', marginBottom: '4px' }}>Filtrar por usuario</label>
          <select value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(1); }} style={selectStyle}>
            <option value="">Todos los usuarios</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <p style={{ color: theme.colors.textMuted }}>Cargando...</p>
      ) : data.length === 0 ? (
        <p style={{ color: theme.colors.textMuted }}>No hay tickets de soporte.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                  {['#', 'Título', 'Estado', 'Asignado', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: theme.colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map(t => (
                  <tr key={t.id} style={{ backgroundColor: ROW_BG[t.status] || 'transparent', borderBottom: `1px solid ${theme.colors.border}22` }}>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, fontFamily: 'monospace', fontSize: '0.78rem' }}>{t.id}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text, fontWeight: 500, maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', backgroundColor: `${STATUS_COLOR[t.status]}22`, color: STATUS_COLOR[t.status] }}>{t.status}</span>
                    </td>
                    <td style={{ padding: '8px 10px', color: t.assigned_username ? theme.colors.text : theme.colors.textMuted }}>{t.assigned_username || 'Sin asignar'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <button onClick={() => openDetail(t.id)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '3px 14px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem' }}>Ver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem', alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>‹</button>
              <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Página {page} de {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>›</button>
            </div>
          )}
        </>
      )}

      {/* Modal detalle del ticket */}
      {detail && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0.6rem' : '1.5rem' }}>
          <div style={{
            backgroundColor: theme.colors.surface, borderRadius: '12px', border: `1px solid ${theme.colors.border}`,
            width: '100%', maxWidth: '980px', maxHeight: '92vh', overflow: 'hidden', boxSizing: 'border-box',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '1rem' : '1.2rem 1.5rem', borderBottom: `1px solid ${theme.colors.border}` }}>
              <h3 style={{ color: theme.colors.primary, margin: 0, fontSize: '1.05rem' }}>Ticket #{detail.id}</h3>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            {loadingDetail ? (
              <p style={{ color: theme.colors.textMuted, padding: '1.5rem' }}>Cargando...</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', overflow: 'auto', flex: 1 }}>
                {/* Panel izquierdo: datos */}
                <div style={{ flex: isMobile ? 'none' : '1 1 45%', padding: isMobile ? '1rem' : '1.3rem', borderRight: isMobile ? 'none' : `1px solid ${theme.colors.border}`, borderBottom: isMobile ? `1px solid ${theme.colors.border}` : 'none', overflow: 'auto' }}>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ color: theme.colors.textMuted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Título</div>
                    <div style={{ color: theme.colors.text, fontWeight: 600, fontSize: '1rem' }}>{detail.title}</div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ color: theme.colors.textMuted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Descripción</div>
                    <div style={{ color: theme.colors.text, fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{detail.description || '—'}</div>
                  </div>
                  {(detail.name || detail.phone) && (
                    <div style={{ marginBottom: '1rem', color: theme.colors.textMuted, fontSize: '0.85rem' }}>
                      {detail.name && <div>👤 {detail.name}</div>}
                      {detail.phone && <div>📞 {detail.phone}</div>}
                    </div>
                  )}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>Asignado a</label>
                    <select value={detail.user_id || ''} disabled={busy} onChange={e => patchTicket({ user_id: e.target.value || null })} style={{ ...selectStyle, width: '100%' }}>
                      <option value="">Sin asignar</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>Estado</label>
                    <select value={detail.status} disabled={busy} onChange={e => patchTicket({ status: e.target.value })} style={{ ...selectStyle, width: '100%', color: STATUS_COLOR[detail.status] || theme.colors.text, fontWeight: 600 }}>
                      {STATUSES.map(s => <option key={s} value={s} style={{ color: theme.colors.text }}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {/* Panel derecho: comentarios estilo chat */}
                <div style={{ flex: isMobile ? 'none' : '1 1 55%', display: 'flex', flexDirection: 'column', minHeight: isMobile ? '340px' : 'auto', maxHeight: isMobile ? '60vh' : 'none' }}>
                  <div style={{ padding: '0.7rem 1.2rem', borderBottom: `1px solid ${theme.colors.border}`, color: theme.colors.textMuted, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Comentarios</div>
                  <div style={{ flex: 1, overflow: 'auto', padding: '1rem 1.2rem', display: 'flex', flexDirection: 'column', gap: '0.7rem', background: theme.colors.background }}>
                    {(!detail.comments || detail.comments.length === 0) && (
                      <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem', textAlign: 'center', marginTop: '1rem' }}>Aún no hay comentarios.</p>
                    )}
                    {(detail.comments || []).map(c => (
                      <div key={c.id} style={{ alignSelf: c.from_client ? 'flex-start' : 'flex-end', maxWidth: '85%' }}>
                        <div style={{
                          background: c.from_client ? theme.colors.surface : 'rgba(59,130,246,0.15)',
                          border: `1px solid ${c.from_client ? theme.colors.border : 'rgba(59,130,246,0.4)'}`,
                          borderRadius: '10px', padding: '8px 11px',
                        }}>
                          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: c.from_client ? '#f59e0b' : '#60a5fa', marginBottom: '2px' }}>
                            {c.from_client ? `👤 ${c.user_name || 'Cliente'}` : c.user_name}
                          </div>
                          {c.text && <div style={{ color: theme.colors.text, fontSize: '0.88rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</div>}
                          {c.media_url && <TicketMedia url={c.media_url} type={c.media_type} name={c.media_name} muted={theme.colors.textMuted} accent={theme.colors.primary} />}
                          <div style={{ fontSize: '0.65rem', color: theme.colors.textMuted, marginTop: '3px' }}>
                            {c.created_at ? new Date(c.created_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                          </div>
                        </div>
                        {!c.from_client && (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', marginTop: '3px', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.colors.textMuted, fontSize: '0.7rem', cursor: 'pointer' }}>
                              <input type="checkbox" checked={!!c.visible_to_client} onChange={e => toggleVisible(c.id, e.target.checked)} style={{ accentColor: theme.colors.primary }} />
                              Visible al cliente
                            </label>
                            <button onClick={() => sendToClient(c.id)} disabled={busy} style={{ background: c.sent_to_client ? 'rgba(34,197,94,0.15)' : '#25D366', color: c.sent_to_client ? '#22c55e' : '#fff', border: 'none', borderRadius: '5px', padding: '2px 9px', fontSize: '0.7rem', cursor: 'pointer', fontWeight: 600 }}>
                              {c.sent_to_client ? '✓ Enviado' : 'Enviar al cliente'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={commentsEndRef} />
                  </div>
                  {feedback && <div style={{ padding: '0 1.2rem', color: feedback.startsWith('✓') ? theme.colors.success : theme.colors.error, fontSize: '0.78rem' }}>{feedback}</div>}
                  {attachment && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 1.2rem', padding: '6px 10px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', fontSize: '0.8rem', color: theme.colors.text }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {attachment.name}</span>
                      <button onClick={() => setAttachment(null)} style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '0.9rem' }}>✕</button>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', padding: '0.8rem 1.2rem', borderTop: `1px solid ${theme.colors.border}`, alignItems: 'flex-end' }}>
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={e => uploadFile(e.target.files[0])} />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Adjuntar archivo" style={{ background: theme.colors.background, color: theme.colors.text, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', padding: '9px 12px', cursor: uploading ? 'wait' : 'pointer', fontSize: '1rem', whiteSpace: 'nowrap' }}>{uploading ? '⏳' : '📎'}</button>
                    <textarea
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(); } }}
                      placeholder="Escribe un comentario..."
                      rows={1}
                      style={{ flex: 1, resize: 'none', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', padding: '9px 11px', color: theme.colors.text, fontSize: '0.88rem', fontFamily: 'inherit', outline: 'none', maxHeight: '120px' }}
                    />
                    <button onClick={sendComment} disabled={busy || uploading || (!newComment.trim() && !attachment)} style={{ background: theme.colors.primary, color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Enviar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Tickets;
